import { api } from '@/lib/api';
// apps/dashboard/src/hooks/use-contacts.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ContactDto {
  id: string;
  tenantId: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  telegramId: string | null;
  profilePhoto: string | null;
  nameLocked: boolean;
  tags: string[];
  optedOut: boolean;
  optedOutAt: string | null;
  optedOutChannel: string | null;
  customFields: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

interface ListContactsResponse {
  data: ContactDto[];
  total: number;
  page: number;
  pageSize: number;
}

interface ContactsFilter {
  search?: string;
  optedOut?: boolean;
  tags?: string[];
  segmentId?: string;
  customFields?: Record<string, string>;
  sortBy?: 'name' | 'phone' | 'email' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export function useContacts(filter: ContactsFilter = {}) {
  const params = new URLSearchParams({ pageSize: String(filter.pageSize ?? 30) });
  if (filter.page) params.set('page', String(filter.page));
  if (filter.search) params.set('search', filter.search);
  if (filter.optedOut !== undefined) params.set('optedOut', String(filter.optedOut));
  if (filter.segmentId) params.set('segmentId', filter.segmentId);
  if (filter.sortBy) params.set('sortBy', filter.sortBy);
  if (filter.sortOrder) params.set('sortOrder', filter.sortOrder);

  if (filter.tags && filter.tags.length > 0) {
    params.set('tags', filter.tags.join(','));
  }

  if (filter.customFields) {
    for (const [key, value] of Object.entries(filter.customFields)) {
      if (value) params.append(`custom_${key}`, value);
    }
  }

  return useQuery({
    queryKey: ['contacts', filter],
    queryFn: () => api.get<ListContactsResponse>(`/contacts?${params}`),
  });
}

export function useContactTags() {
  return useQuery({
    queryKey: ['contacts-tags'],
    queryFn: () => api.get<{ tags: string[] }>('/contacts/tags').then((res) => res.tags),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => api.get<ContactDto>(`/contacts/${id}`),
    enabled: !!id,
  });
}

export function useContactByPhone(phone: string | null | undefined) {
  return useQuery({
    queryKey: ['contacts', 'by-phone', phone],
    queryFn: async () => {
      const params = new URLSearchParams({ search: phone!, pageSize: '1' });
      const res = await api.get<ListContactsResponse>(`/contacts?${params}`);
      return res.data[0] ?? null;
    },
    enabled: !!phone,
    staleTime: 30_000,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name?: string;
      phone?: string;
      email?: string;
      tags?: string[];
    }) => api.post<ContactDto>('/contacts', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ── Kanban / Pipelines ───────────────────────────────────────────────────────

export interface PipelineDto {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  stages?: PipelineStageDto[];
}

export interface PipelineStageDto {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
}

export interface KanbanBoardDto {
  stages: PipelineStageDto[];
  deals: ContactDto[];
}

export function usePipelines() {
  return useQuery({
    queryKey: ['pipelines'],
    queryFn: () => api.get<{ data: PipelineDto[] }>('/contacts/pipelines').then((r) => r.data),
  });
}

export function usePipelineBoard(id: string | null) {
  return useQuery({
    queryKey: ['pipelines', id, 'board'],
    queryFn: () =>
      api.get<{ data: KanbanBoardDto }>(`/contacts/pipelines/${id}/board`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<{ data: PipelineDto }>('/contacts/pipelines', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipelines'] }),
  });
}

export function useMoveDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { contactId: string; stageId: string }) =>
      api.post<{ data: ContactDto }>('/contacts/pipelines/move', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}

export function usePipelineStats(id: string | null) {
  return useQuery({
    queryKey: ['pipelines', id, 'stats'],
    queryFn: () => api.get<{ data: any }>(`/contacts/pipelines/${id}/stats`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useUpdateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, ...body }: { contactId: string; value?: number; status?: string }) =>
      api.patch<{ data: ContactDto }>(`/contacts/pipelines/deals/${contactId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipelines'] }),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      email?: string;
      nameLocked?: boolean;
      tags?: string[];
      customFields?: Record<string, string>;
    }) => api.patch<ContactDto>(`/contacts/${id}`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts', vars.id] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['contact-conversations'] });
    },
  });
}

// ADR-025 — Avatar upload (compensates Meta WA Official limitation)
export function useUpdateContactAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, profilePhoto }: { id: string; profilePhoto: string }) =>
      api.put<ContactDto>(`/contacts/${id}/avatar`, { profilePhoto }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts', vars.id] });
    },
  });
}

export function useBulkAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      contactIds: string[];
      action: 'ADD_TAG' | 'REMOVE_TAG';
      payload: { tagName: string };
    }) => api.post<{ success: boolean; affectedCount: number }>('/contacts/bulk-action', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export interface ContactMemoryDto {
  id: string;
  tenantId: string;
  contactId: string;
  conversationId: string | null;
  content: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useContactMemories(contactId: string) {
  return useQuery({
    queryKey: ['contacts', contactId, 'memories'],
    queryFn: () => api.get<ContactMemoryDto[]>(`/memories/${contactId}`),
    enabled: !!contactId,
  });
}

export function useUpdateContactMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      contactId,
      ...body
    }: {
      id: string;
      contactId: string;
      content?: string;
      isPinned?: boolean;
    }) => api.patch<ContactMemoryDto>(`/memories/${id}`, { ...body, contactId }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts', vars.contactId, 'memories'] });
    },
  });
}

export function useDeleteContactMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; contactId: string }) => api.delete(`/memories/${id}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts', vars.contactId, 'memories'] });
    },
  });
}

interface ContactTimelineMessage {
  id: string;
  direction: string;
  createdAt: string;
  content: { text?: string; body?: string };
}

interface ContactConversationListResponse {
  data: Array<{ id: string }>;
}

interface ContactMessageListResponse {
  data: ContactTimelineMessage[];
}

export function useContactTimelineByPhone(id: string, phone: string | null | undefined) {
  return useQuery({
    queryKey: ['contacts', id, 'timeline', phone],
    queryFn: async () => {
      if (!phone) return [];

      const conversations = await api.get<ContactConversationListResponse>(
        `/conversations?contactPhone=${encodeURIComponent(phone)}&origin=ALL&limit=100`,
      );
      const messageLists = await Promise.all(
        conversations.data.map((conversation) =>
          api.get<ContactMessageListResponse>(
            `/conversations/${conversation.id}/messages?limit=20`,
          ),
        ),
      );

      return messageLists
        .flatMap((response) => response.data)
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
        .slice(0, 20);
    },
    enabled: !!id && !!phone,
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contacts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export type SegmentFilterField =
  | 'tag'
  | 'channel'
  | 'opted_out'
  | 'created_at'
  | 'updated_at'
  | 'custom';

export type SegmentFilter =
  | { field: 'tag'; operator: 'includes' | 'excludes'; value: string }
  | { field: 'channel'; operator: 'eq'; value: string }
  | { field: 'opted_out'; operator: 'eq'; value: boolean }
  | { field: 'created_at' | 'updated_at'; operator: 'before' | 'after'; value: string }
  | { field: 'custom'; key: string; operator: 'eq' | 'contains' | 'gt' | 'lt'; value: string };

export interface SegmentDto {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  filters: SegmentFilter[];
  contactCount: number;
  lastComputedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export function useSegments() {
  return useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<{ data: SegmentDto[] }>('/contacts/segments').then((res) => res.data),
  });
}

export function useSegmentPreview(segmentId: string | null) {
  return useQuery({
    queryKey: ['segments', segmentId, 'preview'],
    queryFn: () =>
      api
        .get<{ data: ContactDto[]; total: number }>(`/contacts/segments/${segmentId}/preview`)
        .then((res) => res),
    enabled: !!segmentId,
  });
}

export function useCreateSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; filters: SegmentFilter[] }) =>
      api.post<{ data: SegmentDto }>('/contacts/segments', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segments'] });
    },
  });
}

export function useUpdateSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      description?: string;
      filters?: SegmentFilter[];
    }) => api.patch<{ data: SegmentDto }>(`/contacts/segments/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segments'] });
    },
  });
}

export function useDeleteSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contacts/segments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segments'] });
    },
  });
}

// ── Attribute Definitions ─────────────────────────────────────────────────────

export interface AttributeDefDto {
  id: string;
  tenantId: string;
  key: string;
  label: string;
  valueType: 'text' | 'number' | 'boolean' | 'date' | 'select';
  isRequired: boolean;
  defaultValue: string | null;
  sortOrder: number;
  options?: string[];
  createdAt: string;
}

export function useAttributeDefs() {
  return useQuery({
    queryKey: ['attribute-defs'],
    queryFn: () =>
      api.get<{ data: AttributeDefDto[] }>('/contacts/attributes/defs').then((r) => r.data),
  });
}

export function useCreateAttributeDef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      key: string;
      label: string;
      valueType: 'text' | 'number' | 'boolean' | 'date' | 'select';
      isRequired?: boolean;
      defaultValue?: string;
      sortOrder?: number;
      options?: string[];
    }) => api.post<{ data: AttributeDefDto }>('/contacts/attributes/defs', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attribute-defs'] });
    },
  });
}

export function useUpdateAttributeDef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      ...body
    }: {
      key: string;
      label?: string;
      isRequired?: boolean;
      defaultValue?: string;
      sortOrder?: number;
      options?: string[];
    }) => api.patch<{ data: AttributeDefDto }>(`/contacts/attributes/defs/${key}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attribute-defs'] });
    },
  });
}

export function useDeleteAttributeDef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.delete(`/contacts/attributes/defs/${key}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attribute-defs'] });
    },
  });
}

// ── Contact Attribute Values ──────────────────────────────────────────────────

export interface ContactAttributeValueDto {
  key: string;
  label: string;
  valueType: 'text' | 'number' | 'boolean' | 'date' | 'select';
  value: string;
  options: string[];
  sortOrder: number;
  isRequired: boolean;
}

export interface ContactAttributesResponseDto {
  data: Record<string, string>;
  attributes: ContactAttributeValueDto[];
}

export function useContactAttributes(contactId: string) {
  return useQuery({
    queryKey: ['contact-attributes', contactId],
    queryFn: () =>
      api
        .get<ContactAttributesResponseDto>(`/contacts/${contactId}/attributes`)
        .then((response) => response),
    enabled: !!contactId,
  });
}

export function useUpdateContactAttributes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      attributes,
    }: {
      contactId: string;
      attributes: Record<string, string>;
    }) =>
      api
        .put<{ data: Record<string, string> }>(`/contacts/${contactId}/attributes`, {
          values: Object.entries(attributes).map(([key, value]) => ({ key, value })),
        })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contact-attributes', vars.contactId] });
      qc.invalidateQueries({ queryKey: ['contacts', vars.contactId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

// ── Pre-Chat Forms ────────────────────────────────────────────────────────────

export interface PreChatFieldDto {
  id: string;
  type: 'text' | 'email' | 'phone' | 'select' | 'textarea';
  label: string;
  placeholder?: string;
  isRequired: boolean;
  options?: string[];
  mappedAttribute?: string;
}

export interface PreChatFormDto {
  id: string;
  tenantId: string;
  inboxId: string;
  title: string;
  description?: string;
  fields: PreChatFieldDto[];
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export function usePreChatForm(inboxId: string) {
  return useQuery({
    queryKey: ['pre-chat-form', inboxId],
    queryFn: () =>
      api
        .get<{ data: PreChatFormDto | null }>(`/contacts/pre-chat-forms/${inboxId}`)
        .then((r) => r.data),
    enabled: !!inboxId,
  });
}

export function useUpsertPreChatForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inboxId,
      ...body
    }: {
      inboxId: string;
      title: string;
      description?: string;
      fields: PreChatFieldDto[];
      isEnabled: boolean;
    }) => api.put<{ data: PreChatFormDto }>(`/contacts/pre-chat-forms/${inboxId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pre-chat-form'] });
    },
  });
}
